<template>
  <v-row class="">
    <div class="flow-line">
      <div class="pt-8 flow-line-inside">&nbsp;</div>
    </div>
    <v-card outlined class="mx-auto py-1 px-8" width="95%" elevation="1">
      <v-row class="">
        <v-col cols="11" class="pt-0 px-0 mx-0"
          ><v-card-title>Interactive Voice Response (IVR)</v-card-title>
        </v-col>
        <v-col cols="1" class="px-0"></v-col>
      </v-row>

      <v-row class="pa-3">
        <v-col cols="12">
          <v-radio-group
            :disabled="PreviewMode"
            v-model="isTTS"
            row
            dense
            @change="updateRecordedMessageType"
          >
            <v-radio label="Text to Speech" :value="true"></v-radio>
            <v-radio label="Upload Audio File" :value="false"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col
          cols="12"
          class="my-0 py-0"
          v-if="recordedMessageType == 'audiofile'"
        >
          <v-sheet>
            <v-file-input
              :disabled="PreviewMode"
              :accept="acceptAudioFormats"
              label="Audio File for Greeting"
              hint="Upload a MP3 or WAV file"
              persistent-hint
              dense
              aria-required="Greeting text is required"
              required
              :rules="[rules.required]"
              @change="onAudioUrlChanged"
              @click:clear="audioUrl = ''"
              outlined
            ></v-file-input>
            <v-lazy>
              <audio
                v-if="audioUrl"
                controls
                :src="`${recordingUrl}`"
                @play="setPlayerStatus('play')"
                @pause="setPlayerStatus('pause')"
                @ended="setPlayerStatus('ended')"
                class="recording-controls"
              ></audio>
            </v-lazy>
          </v-sheet>
        </v-col>
        <v-col cols="12" class="my-0 py-0" v-if="recordedMessageType == 'tts'">
          <v-textarea
            :disabled="PreviewMode"
            label="Text to Speech Greeting"
            v-model="ttsText"
            @input="onUpdateAnything"
            class="py-1"
            dense
            auto-grow
            rows="2"
            aria-required="Greeting text is required"
            required
            :rules="[rules.required]"
            outlined
          ></v-textarea>
          <v-lazy>
            <audio
              v-if="ttsAudioUrl"
              controls
              :src="`${recordingUrl}`"
              @play="setPlayerStatus('play')"
              @pause="setPlayerStatus('pause')"
              @ended="setPlayerStatus('ended')"
              class="recording-controls"
            ></audio>
          </v-lazy>
        </v-col>

        <v-col>
          <v-list-item-title class="d-flex justify-space-between">
            <div class="mb-2">IVR Options</div>
            <div class="mb-2">
              <v-btn
                v-if="!PreviewMode"
                outlined
                small
                class=""
                color="primary"
                @click="newIVROption"
                >New IVR Option</v-btn
              >
            </div>
          </v-list-item-title>
        </v-col>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="responses"
            class="elevation-0"
          >
            <template v-slot:item.rank="{ item }">
              <v-icon small class="mr-2" @click="onArrowUp(item)">
                mdi-arrow-up-bold
              </v-icon>
              <v-icon small @click="onArrowDown(item)">
                mdi-arrow-down-bold
              </v-icon>
            </template>

            <template v-slot:item.redirectTo="{ item }">
              {{ nameForFlow(item.redirectTo) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="onEditItem(item, $event)">
                mdi-pencil
              </v-icon>
              <v-icon small @click="onDeleteItem(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-col>

        <v-col cols="12">
          <v-text-field
            :disabled="PreviewMode"
            label="Response Timeout (in seconds)"
            hint="If this timeout passes we'll continue to the next step in the call flow."
            persistent-hint
            @input="onUpdateAnything"
            required
            :rules="[rules.required, rules.number]"
            v-model="timeoutSeconds"
            v-mask="masks.number"
            outlined
          ></v-text-field>
        </v-col>
      </v-row>

      <v-btn
        :disabled="PreviewMode"
        text
        icon
        color="error"
        @click="onRemoveMe"
        class="float-right"
      >
        Remove
      </v-btn>
    </v-card>
    <v-dialog v-model="ivrOptionDialog" max-width="550px" persistent>
      <call-flow-i-v-r-option-modal
        v-if="ivrOptionDialog"
        ref="ivr_option_modal"
        :FlowId="FlowId"
        :config="config"
        :ordinal="optionOrdinal"
        :flowList="flowList"
        :onIVROptionUpdate="onIVROptionUpdate"
      ></call-flow-i-v-r-option-modal>
    </v-dialog>
  </v-row>
</template>

<script>
import { VALIDATION_RULES } from "../../../helpers/ValidationRules";
import hashCode from "../../../helpers/hashCode";
import { API_BASE_URL } from "../../../globalVars";
import axios from "axios";
import { MASKS } from "../../../helpers/Masks";
import { mask } from "vue-the-mask";
import CallFlowIVROptionModal from "./CallFlowIVROptionModal";

export default {
  name: "CallFlowIVR",
  components: {
    CallFlowIVROptionModal
  },
  directives: {
    mask
  },
  props: {
    FlowId: {
      type: Number,
      required: true
    },
    order: {
      type: Number,
      required: true
    },
    updateCallback: {
      type: Function,
      required: false
    },
    onRemove: {
      type: Function,
      required: true
    },
    flowList: {
      type: Array,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    PreviewMode: {
      type: Boolean,
      required: true
    }
  },
  data: function() {
    return {
      rules: VALIDATION_RULES,
      masks: MASKS,

      showIVRDialog: false,
      ivrOptionDialog: false,
      optionOrdinal: null,

      acceptAudioFormats: "audio/mp3,audio/mpeg,audio/x-wav",
      playerStatus: "new",
      isTTS: true,

      recordedMessageType: "tts",
      ttsText: "",
      audioUrl: null,
      ttsAudioUrl: null,
      recordingUrl: null,

      timeoutSeconds: 15,
      numDigits: 1,
      inputType: "dtmf speech",
      responses: []
    };
  },
  computed: {
    account() {
      return this.$store.getters["entities/accounts/query"]()
        .withAllRecursive(1)
        .first();
    },
    callFlowType() {
      const listType = this.$store.getters["entities/listTypes/query"]()
        .withAllRecursive(1)
        .where("code", value => ["call_flow"].includes(value))
        .first();
      return listType;
    },
    forwardingPhoneTypeId() {
      let listType = this.$store.getters["entities/listTypes/query"]()
        .where("code", value => ["forwarding_number"].includes(value))
        .first();
      if (listType && listType.id) {
        return listType.id;
      }
      return null;
    },
    forwardingPhones() {
      const listType = this.$store.getters["entities/listTypes/query"]()
        .withAllRecursive(1)
        .where("code", value => ["forwarding_number"].includes(value))
        .first();
      return listType;
    },
    headers() {
      let headers = [
        { text: "Order", value: "rank", sortable: false },
        {
          text: "Key Pressed",
          align: "start",
          value: "digit",
          sortable: false
        },
        // {
        //   text: "Spoken Response",
        //   align: "start",
        //   value: "verbal",
        //   sortable: false
        // },
        {
          text: "Next Flow",
          align: "start",
          value: "redirectTo",
          sortable: false
        }
      ];
      if (!this.PreviewMode) {
        headers.push({ text: "Action", value: "actions", sortable: false });
      }
      return headers;
    }
  },
  created() {
    if (
      this.order != null &&
      this.flowList.length > 0 &&
      this.flowList[this.order] &&
      this.flowList[this.order].recordedMessageType
    ) {
      const tempData = Object.assign({}, this.flowList[this.order]);
      this.recordedMessageType = tempData.recordedMessageType;
      this.ttsText = tempData.ttsText;
      this.audioUrl = tempData.audioUrl;
      this.updateRecordingUrl(this.audioUrl);
      this.timeoutSeconds = tempData.timeoutSeconds;
      this.numDigits = tempData.numDigits;
      this.inputType = tempData.inputType;
      for (
        let i = 0;
        tempData.responses &&
        tempData.responses.length &&
        i < tempData.responses.length;
        i++
      ) {
        tempData.responses[i].rank = i;
      }
      this.responses = tempData.responses;

      this.isTTS = this.recordedMessageType == "tts";

      if (this.recordedMessageType == "tts") {
        const hashCodeFromText = hashCode(this.ttsText);
        this.ttsAudioUrl = `telephony/call-flow/${tempData.uid}/${hashCodeFromText}.mp3`;
        this.updateRecordingUrl(this.ttsAudioUrl);
      }
    }
  },
  methods: {
    nameForFlow(id) {
      const flow = this.callFlowType.items.filter(item => {
        return item.meta.uid == id;
      });
      return flow && flow[0] && flow[0].name ? flow[0].name : "Nameless";
    },
    updateRecordedMessageType() {
      if (this.isTTS) {
        this.recordedMessageType = "tts";
      } else {
        this.recordedMessageType = "audiofile";
      }
      this.recordingUrl = null;
      this.onUpdateAnything();
    },
    onAudioUrlChanged(args) {
      let recordingFile = args;
      this.uploadRecordingFile({
        type: "call-flow",
        recordingFile,
        recordingType: "ivr"
      });
    },
    uploadRecordingFile({ type, recordingFile, recordingType }) {
      if (!recordingFile) {
        return;
      }
      // do the upload
      let vueThis = this;
      let formData = new FormData();
      formData.append("file", recordingFile);
      axios({
        method: "get",
        url:
          API_BASE_URL +
          `/file-upload/telecom-recording?type=${type}&contenttype=${
            recordingFile.type
          }&name=${encodeURIComponent(recordingFile.name)}&uid=${
            vueThis.config.uid
          }`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      })
        .then(response => {
          axios
            .put(response.data.url, recordingFile, {
              headers: {
                "Content-Type": recordingFile.type
              }
            })
            .then(function(putResponse) {
              const url = putResponse.config.url.split("?")[0].split("/");
              const newValue = [url[6], url[7], url[8], url[9]].join("/");
              if (recordingType == "call-recording") {
                vueThis.recordingAudioUrl = newValue;
              } else if (recordingType == "call-whisper") {
                vueThis.whisperAudioUrl = newValue;
              } else if (recordingType == "greeting") {
                vueThis.audioUrl = newValue;
              } else if (recordingType == "ivr") {
                vueThis.audioUrl = newValue;
              } else {
                console.error("oops no recordingType");
              }
              vueThis.updateRecordingUrl(newValue);
              vueThis.onUpdateAnything();
            })
            .catch(function(error) {
              console.error("Unable to upload file ", error);
            });
        })
        .catch(error => {
          console.error("Unable to get an signed upload URL", error);
        });
    },
    updateRecordingUrl(path) {
      let vueThis = this;
      const externalId = this.account.externalId;
      axios({
        method: "get",
        url:
          API_BASE_URL +
          `/account?do=file-url&externalId=${externalId}&path=account/${externalId}/${path}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      }).then(response => {
        vueThis.recordingUrl =
          response && response.data ? response.data.url : null;
      });
    },
    setPlayerStatus(status) {
      this.playerStatus = status;
    },
    onRemoveMe() {
      this.onRemove(this.config.uid);
    },
    onUpdateAnything() {
      const newConfig = Object.assign({}, this.config);
      newConfig.order = this.order;
      newConfig.recordedMessageType = this.recordedMessageType;
      newConfig.ttsText = this.ttsText;
      newConfig.audioUrl = this.audioUrl;
      newConfig.timeoutSeconds = this.timeoutSeconds;
      newConfig.numDigits = this.numDigits;
      newConfig.inputType = this.inputType;
      newConfig.responses = this.responses;
      // this.updateRecordingUrl(this.audioUrl);
      this.updateCallback(this.order, newConfig);
    },
    newIVROption() {
      this.optionOrdinal = null;
      this.showIVRDialog = true;
      this.ivrOptionDialog = true;
    },
    onEditItem(item, event) {
      this.optionOrdinal = item.rank;
      this.showIVRDialog = true;
      this.ivrOptionDialog = true;
    },
    onIVROptionUpdate(responses) {
      this.responses = responses;
      this.ivrOptionDialog = false;
      this.optionOrdinal = null;
      this.showIVRDialog = false;
      this.onUpdateAnything();
    },
    onArrowUp(item) {
      if (item.rank === 0) {
        return;
      }
      let listItems = [...this.responses];
      if (item.rank) {
        listItems.splice(item.rank - 1, 0, listItems.splice(item.rank, 1)[0]);
      }
      this.responses = this.updateOptionRank(listItems);
      this.onUpdateAnything();
    },
    onArrowDown(item) {
      if (item.rank && item.rank >= this.responses.length - 1) {
        return;
      }
      let listItems = [...this.responses];
      if (item.rank >= 0) {
        listItems.splice(item.rank + 1, 0, listItems.splice(item.rank, 1)[0]);
      }
      this.responses = this.updateOptionRank(listItems);
      this.onUpdateAnything();
    },
    onDeleteItem(item) {
      let listItems = [...this.responses];
      listItems.splice(item.rank, 0);
      this.responses = this.updateOptionRank(listItems);
      this.onUpdateAnything();
    },
    updateOptionRank(responses) {
      for (let i = 0; responses.length && i < responses.length; i++) {
        responses[i].rank = i;
      }
      return responses;
    }
  },
  watch: {
    ttsText(newVal, oldVal) {
      if (oldVal != "" && oldVal != newVal) {
        this.ttsAudioUrl = null;
      }
    }
  }
};
</script>

<style scoped lang="stylus">
.call-flow-icon {
  color: #1976d2 !important;
  caret-color: #1976d2 !important;
  font-weight: 400;
}
.flow-line {
  height: 3rem;
  width: 100%;
}
.flow-line-inside {
  height: 3rem;
  width: 50%;
  border-right: 1px solid #cccccc;
}
.selected-phone {
  padding-left: .5rem;
}
span.double-chip  span.v-chip__content{
  display: flex;
}
</style>
